import React, { useEffect, useState } from "react";
import { DomainValue } from "../../../../../../services/mdmService/types";
import "./masterData.css";
import MasterRenderCard from "./masterRenderCard";
import { Button } from "@mui/material";

interface MasterDataTableProps {
  mdmData: DomainValue[];
  setMdmData: React.Dispatch<React.SetStateAction<DomainValue[]>>;
  setSelectedMaster: React.Dispatch<
    React.SetStateAction<DomainValue | undefined>
  >;
  selectedMaster: DomainValue | undefined;
  handleScreenSwitch: (
    screen: "default" | "logs" | "edit" | "division"
  ) => void;
}

interface MastersProps {
  data: DomainValue[];
  setSelectedMaster: React.Dispatch<
    React.SetStateAction<DomainValue | undefined>
  >;
  selectedMaster: DomainValue;
  handleScreenSwitch: (
    screen: "default" | "logs" | "edit" | "division"
  ) => void;
}

const MastersComponent: React.FC<MastersProps> = ({
  data,
  setSelectedMaster,
  selectedMaster,
  handleScreenSwitch,
}) => {
  const [expandedMandatoryCard, setExpandedMandatoryCard] = useState<
    number | null
  >(null);
  const [expandedOptionalCard, setExpandedOptionalCard] = useState<
    number | null
  >(null);

  // Separate Mandatory and Optional Masters
  const mandatoryMasters = data.filter((master) => !master.isOptionalMaster);
  const optionalMasters = data.filter((master) => master.isOptionalMaster);

  const groupByParentTemplate = (masters: DomainValue[]) => {
    const groupedMasters: {
      [key: string]: {
        masters: DomainValue[];
        description: string;
        icon: string;
        multipleOptionMasters?: DomainValue[];
      };
    } = {};
  
    // Temporary array to collect PJP masters
    const pjpMasters: DomainValue[] = [];
  
    masters.forEach((master) => {
      if (master.id === "PJPVisitPlan" || master.id === "PJPVisitPlanV2") {
        // Collect PJP masters to add them as a single group later
        pjpMasters.push(master);
      } else {
        const groupKey = master.parentTemplate || master.id;
        const displayGroupName = master.parentTemplate
          ? master.parentTemplate
          : master.name;
        const displayGroupDescription = master.parentDescription ?? "";
        const displayGroupIcon = master.parentIcon ?? "";
  
        // Initialize group if it doesn't exist
        if (!groupedMasters[displayGroupName]) {
          groupedMasters[displayGroupName] = {
            masters: [],
            description: displayGroupDescription,
            icon: displayGroupIcon,
          };
        }
  
        // Add master to the group
        groupedMasters[displayGroupName].masters.push(master);
      }
    });
  
    // Add the PJP masters as a single group entry
    if (pjpMasters.length > 0) {
      groupedMasters["PJPVisitPlan"] = {
        masters:  [pjpMasters[0]],
        description: pjpMasters[0].description ?? "",
        icon: pjpMasters[0].icon ?? "",
        multipleOptionMasters: pjpMasters,
      };
    }
  
    return groupedMasters;
  };
  

  useEffect(() => {
    if (selectedMaster) {
      const groupKey = selectedMaster.parentTemplate || selectedMaster.id;

      // Check if the selected master is in the mandatory masters group
      const mandatoryGroupIndex = Object.keys(
        groupedMandatoryMasters
      ).findIndex((groupName) => groupName === groupKey);

      // Check if the selected master is in the optional masters group
      const optionalGroupIndex = Object.keys(groupedOptionalMasters).findIndex(
        (groupName) => groupName === groupKey
      );

      // Expand the correct mandatory card if found
      if (mandatoryGroupIndex !== -1) {
        setExpandedMandatoryCard(mandatoryGroupIndex);
      }

      // Expand the correct optional card if found
      if (optionalGroupIndex !== -1) {
        setExpandedOptionalCard(optionalGroupIndex);
      }
    }
  }, [selectedMaster]);

  const groupedMandatoryMasters = groupByParentTemplate(mandatoryMasters);
  const groupedOptionalMasters = groupByParentTemplate(optionalMasters);

  return (
    <div style={{ gap: "20px", paddingTop: "20px", paddingBottom: "20px" }}>
      {/* Mandatory Masters */}
      <div className="mandatory-master" style={{ flex: 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <h2>Mandatory Masters</h2>
          {Object.values(groupedMandatoryMasters).some((group) =>
            group.masters.some((master) => master.id === "Division")
          ) && (
            <Button
              variant="outlined"
              onClick={(e) => {
                handleScreenSwitch("division");
              }}
              sx={{
                color: "#434343",
                borderColor: "#CBCBCB",
                padding: "5px",
                borderRadius: "10px",
                margin: "5px",
                marginRight: "15px",
              }}
            >
              {`Update Division >`}
            </Button>
          )}
        </div>
        {Object.keys(groupedMandatoryMasters).map((groupName, index) => {
          // Ensure you're checking the masters array correctly
          const currentMasters =
            groupedMandatoryMasters[groupName]?.masters || [];

          // Filter out the "Division" master by ID
          const filteredMasters = currentMasters.filter(
            (master) => master.id !== "Division"
          );
          // && master.id !== "PJPVisitPlan"

          // Check if there are masters left after filtering
          if (filteredMasters.length > 0) {
            return (
              <MasterRenderCard
                key={index}
                index={index}
                groupedMasters={filteredMasters}
                currentMasterGroupName={groupName}
                setExpandedMasterIndex={setExpandedMandatoryCard}
                expandedMasterIndex={expandedMandatoryCard}
                groupedMandatoryMasters={groupedMandatoryMasters}
                selectedMaster={selectedMaster}
                setSelectedMaster={setSelectedMaster}
              />
            );
          }
          return null; // Skip rendering this group if no masters remain
        })}
      </div>

      {/* Optional Masters */}
      <div className="optional-master" style={{ flex: 1 }}>
        <h2>Optional Masters</h2>
        {Object.keys(groupedOptionalMasters).map((groupName, index) => (
          <MasterRenderCard
            index={index}
            groupedMasters={optionalMasters}
            currentMasterGroupName={groupName}
            setExpandedMasterIndex={setExpandedOptionalCard}
            expandedMasterIndex={expandedOptionalCard}
            groupedMandatoryMasters={groupedOptionalMasters}
            selectedMaster={selectedMaster}
            setSelectedMaster={setSelectedMaster}
          />
        ))}
      </div>
    </div>
  );
};

const MasterDataTable = (props: MasterDataTableProps) => {
  return props.mdmData !== undefined && props.mdmData !== null ? (
    <MastersComponent
      data={props.mdmData}
      setSelectedMaster={props.setSelectedMaster}
      selectedMaster={props.selectedMaster!}
      handleScreenSwitch={props.handleScreenSwitch}
    />
  ) : (
    <></>
  );
};

export default MasterDataTable;
